import { cloneElement, isValidElement, type JSX } from 'react';

import { cn } from '../../../../v2';
import { CardDescription, CardTitle } from '../../../../v2/primitives/card';
import { ExpandableBody, ExpandableRow } from '../../../expandable-row';
import type { BaseCardProps } from '../../types';
import { SelectableControl } from '../selectable-control';
import { TitleChevron } from '../title-chevron';

export function CompactSizeDetailCard(props: BaseCardProps): JSX.Element {
  const {
    actions,
    assistiveText,
    children,
    disabledProps,
    expanded,
    hasActions,
    isExpandable,
    isHighlighted,
    isSelectable,
    isClickable,
    selectableOptions,
    setExpanded,
    showSubtitleComponentWhenExpanded,
    subtitle,
    testId,
    title,
    cardProps,
  } = props;
  const { isDisabled } = disabledProps ?? {};

  const { titleClassName: cardTitleClassName, highlightProps } = cardProps ?? {};
  const titleClassName = cn('text-foreground', cardTitleClassName);

  const { titleClassName: highlightTitleClassName } = highlightProps ?? {};
  const currentTitleClassName = cn(titleClassName, {
    [cn('text-brand-400 dark:text-foreground', highlightTitleClassName)]: isHighlighted,
  });

  return (
    <ExpandableRow removeStyling expanded={expanded} onToggleRow={(value) => setExpanded(!value)}>
      <div className="flex flex-row items-start">
        {isExpandable && (
          <button onClick={() => setExpanded(!expanded)} type="button">
            <TitleChevron testId={testId} rotated={expanded} className={titleClassName} />
          </button>
        )}
        <div className="flex flex-1 flex-col">
          <div className="flex flex-row justify-between gap-1">
            <button onClick={() => setExpanded(!expanded)} type="button" className="inline-block">
              <CardTitle
                className={cn(
                  'text-start font-bold break-words',
                  {
                    'line-clamp-2': !expanded,
                  },
                  currentTitleClassName,
                )}
                data-testid={`${testId}-title`}
              >
                {title}
              </CardTitle>
            </button>
            {isSelectable ? (
              <div className="flex flex-row items-start">
                <SelectableControl selectableOptions={selectableOptions} disabled={isDisabled} />
              </div>
            ) : (
              <div className="flex flex-row gap-1">
                {hasActions && (
                  <>
                    {actions?.map((action, i) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`action-for-${title}-${i}`}
                        {...(isClickable && {
                          onClick: (e) => e.stopPropagation(),
                          role: 'button',
                          tabIndex: 0,
                          onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                            }
                          },
                        })}
                      >
                        {isDisabled && isValidElement(action)
                          ? cloneElement<{ disabled?: boolean }>(action as JSX.Element, { disabled: isDisabled })
                          : action}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          {subtitle && (!expanded || (expanded && showSubtitleComponentWhenExpanded)) && (
            <CardDescription
              className={cn({
                'line-clamp-1': !expanded,
              })}
              data-testid={`${testId}-subtitle`}
            >
              {subtitle}
            </CardDescription>
          )}
        </div>
      </div>
      <ExpandableBody paddingX={0} paddingBottom={0}>
        <div className="p-1">
          {children}
          {assistiveText && (
            <div className="pt-1">
              <span className="text-sm font-light" data-testid={`${testId}-assistive-text`}>
                {assistiveText}
              </span>
            </div>
          )}
        </div>
      </ExpandableBody>
    </ExpandableRow>
  );
}
